import { useEffect, useState } from "react";
import { Upload, Trash2, Copy, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "../lib/api";

function formatBytes(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

export default function MediaLibrary() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/media");
      setItems(data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load media");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    let ok = 0;
    const errors = [];
    try {
      for (const f of files) {
        try {
          const fd = new FormData();
          fd.append("file", f);
          await api.post("/admin/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
          ok += 1;
        } catch (err) {
          errors.push(`${f.name}: ${err?.response?.data?.detail || err?.message || "Upload failed"}`);
        }
      }
      if (ok) toast.success(`Uploaded ${ok} of ${files.length} file${files.length > 1 ? "s" : ""}`);
      errors.forEach((m) => toast.error(m));
      load();
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const onDelete = async (filename) => {
    if (!window.confirm(`Delete ${filename}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/media/${filename}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const onCopy = async (url) => {
    const fullUrl = url.startsWith("http") ? url : `${API.replace(/\/api$/, "")}${url}`;
    try {
      await navigator.clipboard.writeText(fullUrl);
      toast.success("URL copied");
    } catch {
      toast.error("Could not copy");
    }
  };

  return (
    <div data-testid="media-library">
      <header className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="ed-overline mb-2">Content</p>
          <h1 className="font-serif text-3xl sm:text-4xl text-[var(--ed-ink)]">Media Library</h1>
          <p className="text-[var(--ed-ink-muted)] text-sm mt-1.5">
            {items.length} file{items.length !== 1 ? "s" : ""} · iPhone HEIC photos are converted automatically to optimised JPEG.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={load}
            className="text-[0.65rem] uppercase tracking-[0.22em] border border-[var(--ed-rule)] hover:border-[var(--ed-ink)] text-[var(--ed-ink-soft)] hover:text-[var(--ed-ink)] px-3 py-2.5 rounded-sm flex items-center gap-1.5"
          >
            <RefreshCw size={12} /> Refresh
          </button>
          <label className="bg-[var(--ed-emerald)] text-white hover:bg-[var(--ed-emerald-deep)] py-2.5 px-5 text-[0.65rem] uppercase tracking-[0.22em] rounded-sm cursor-pointer flex items-center gap-2">
            <Upload size={12} /> {uploading ? "Uploading…" : "Upload files"}
            <input
              data-testid="media-upload-input"
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/webp,image/heic,image/heif,.heic,.heif"
              multiple
              onChange={onUpload}
              className="hidden"
            />
          </label>
        </div>
      </header>

      {loading ? (
        <p className="text-[var(--ed-ink-muted)] py-16 text-center">Loading…</p>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-[var(--ed-rule)] rounded-sm p-16 text-center">
          <Upload size={26} className="mx-auto mb-4 text-[var(--ed-ink-muted)]" />
          <p className="text-[var(--ed-ink-muted)]">No files yet. Upload your first photograph to get started.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {items.map((it) => (
            <div
              key={it.filename}
              data-testid={`media-item-${it.filename}`}
              className="border border-[var(--ed-rule)] rounded-sm overflow-hidden bg-[var(--ed-bg)] group"
            >
              <div className="aspect-square overflow-hidden bg-[var(--ed-paper)]">
                <img src={it.url} alt={it.filename} className="w-full h-full object-cover" loading="lazy" />
              </div>
              <div className="p-3">
                <p className="text-[var(--ed-ink)] text-xs truncate" title={it.filename}>{it.filename}</p>
                <p className="text-[var(--ed-ink-muted)] text-[0.65rem] mt-0.5">{formatBytes(it.size)}</p>
                <div className="flex gap-1.5 mt-2.5">
                  <button
                    onClick={() => onCopy(it.url)}
                    className="flex-1 text-[0.55rem] uppercase tracking-widest border border-[var(--ed-rule)] hover:border-[var(--ed-emerald)] hover:text-[var(--ed-emerald)] text-[var(--ed-ink-soft)] px-2 py-1.5 rounded-sm flex items-center justify-center gap-1"
                  >
                    <Copy size={10} /> Copy URL
                  </button>
                  <button
                    onClick={() => onDelete(it.filename)}
                    data-testid={`media-delete-${it.filename}`}
                    className="text-[0.55rem] border border-red-200 hover:border-red-500 text-red-500 hover:bg-red-50 px-2 py-1.5 rounded-sm flex items-center justify-center"
                    aria-label="Delete"
                  >
                    <Trash2 size={10} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
